/* eslint-disable @typescript-eslint/no-explicit-any */
// url to your service

import { Dispatch, useContext, useEffect, useMemo, useRef } from 'react'
import { Message } from 'google-protobuf'
import {
  USE_CASE,
  USER_ROLE,
} from '@trustero/trustero-api-web/lib/account/account_pb'
import { ToastPrompts } from 'src/Utils/helpers/toast'
import { toast } from 'react-toastify'
import log from 'loglevel'
import {
  getAuthorizationCacheScope,
  getRpcAccess,
  getRpcPath,
  rpcAuthorization,
  RpcAccessDenied,
} from '../app/AppAuth/rpcAuthorization'
import AuthContext, {
  AuthAction,
  AuthActionType,
  AuthRecord,
} from '../context/authContext'
import ContentContext, { Content } from '../context/ContentContext'
import { GrpcCall } from '../components/async/utils'
import {
  useGrpcRevalidateByMethod,
  useGrpcRevalidateByMethodRequest,
} from '../components'
import {
  authHeader,
  NTRCE_API_URL,
  translateTokenError,
  attemptTokenRefresh,
} from './gRpcAdapter'
import { callApiAndUpdateContent } from './dataModelAdapterUtils'
import { getRefererHeader } from './referer'
import { getSwrRequestIdHeader } from './requestIdHeader'
import { createRpcSession, CurrentAuth, useCurrentAuth } from './rpcSession'

export type GrpcClientCons<T> = {
  new (arg1: string, arg2: any, arg3: any): T
}

export type GrpcClient<T> = T & {
  header: { Authorization?: string }
  accountId?: string
}

export const authorizedGrpcClientFactory = <T>(
  clientCons: GrpcClientCons<T>,
  auth?: AuthRecord | null,
  authDispatch?: Dispatch<AuthAction> | null,
  currentAuth?: CurrentAuth,
  streams?: Set<{ cancel: () => void }>,
): GrpcClient<T> => {
  const client = new clientCons(NTRCE_API_URL, null, null)

  const scope = getAuthorizationCacheScope(auth)
  const isCurrent = () =>
    !currentAuth ||
    (currentAuth()?.accessToken === auth?.accessToken &&
      getAuthorizationCacheScope(currentAuth()) === scope)
  const header = {
    ...authHeader(auth?.accessToken),
    ...getRefererHeader(),
  }
  for (const prop in client) {
    if (typeof client[prop] !== 'function') {
      continue
    }

    // eslint-disable-next-line @typescript-eslint/ban-types
    const original = client[prop] as unknown as Function
    client[prop] = ((...args: any[]) => {
      if (getRpcAccess(original, auth, args[0]) !== 'allowed' || !isCurrent()) {
        return rejectRpc(original)
      }
      const session = createRpcSession(auth, currentAuth, original, args[0])
      const metadata = {
        ...args[1],
        ...header,
      }
      args[1] = {
        ...metadata,
        ...getSwrRequestIdHeader(metadata),
      }

      const call = original.call(client, ...args)

      // Check if it's a Promise or a Stream
      // Promise is a standard API call
      if (typeof call.then === 'function') {
        // It's a Promise
        return call
          .then((response: unknown) => {
            if (!session.isCurrent()) throw new RpcAccessDenied(original)
            return response
          })
          .catch(async (err: Error & { code?: number }) => {
            if (!session.isCurrent() && !session.canRecover(err))
              throw new RpcAccessDenied(original)
            if (err instanceof RpcAccessDenied) throw err
            if (
              err?.code === 7 &&
              err.message.includes('this account is read only')
            ) {
              toast(ToastPrompts.READ_ONLY_ACCOUNT)
            }

            const parsedErrors:
              | {
                  token: any
                  general: any
                  void?: boolean
                  shouldRefresh?: boolean
                }
              | {
                  token: any
                  void?: boolean
                  shouldRefresh?: boolean
                } = translateTokenError(err)

            log.debug(
              'authorizedGrpcClientFactory: error code:',
              err?.code,
              'parsedErrors:',
              parsedErrors,
            )

            if (parsedErrors.token) {
              let logoutToken = auth?.accessToken
              // If the error is due to token/permission version mismatch, try refreshing first
              if (parsedErrors.shouldRefresh) {
                log.info(
                  'Token/permission version error detected, attempting refresh',
                )
                const newToken = await attemptTokenRefresh(auth, authDispatch)
                if (newToken) {
                  await session.acceptRefresh(newToken)
                  logoutToken = newToken
                  log.info('Retrying original request with refreshed token')
                  // Retry the original request with the new token
                  try {
                    if (!session.isCurrent())
                      throw new RpcAccessDenied(original)
                    const newHeader = {
                      ...authHeader(newToken),
                      ...getRefererHeader(),
                    }
                    const metadata = {
                      ...args[1],
                      ...newHeader,
                    }
                    args[1] = {
                      ...metadata,
                      ...getSwrRequestIdHeader(metadata),
                    }
                    const response = await original.call(client, ...args)
                    if (!session.isCurrent())
                      throw new RpcAccessDenied(original)
                    return response
                  } catch (retryErr) {
                    if (!session.isCurrent())
                      throw new RpcAccessDenied(original)
                    if (retryErr instanceof RpcAccessDenied) throw retryErr
                    const retryErrors = translateTokenError(retryErr)
                    if (!retryErrors.token || retryErrors.shouldRefresh)
                      throw retryErr
                  }
                } else {
                  log.warn('Token refresh failed, will logout')
                }
              }
              // If refresh didn't work or wasn't applicable, logout
              if (!session.isCurrent()) throw new RpcAccessDenied(original)
              log.info('Logging out due to token error')
              authDispatch &&
                authDispatch({
                  type: AuthActionType.LOGOUT,
                  sourceAccessToken: logoutToken,
                  sourceAccountId: auth?.accountId,
                  useCase: USE_CASE.SAAS_BUYER,
                  role: USER_ROLE.READONLY,
                  logoutReason: 'grpc_token_error',
                })
              return
            }

            if (parsedErrors.void === true) {
              return
            }

            throw err
          })
      } else {
        // Streams may outlive the role/account that opened them.
        streams?.add(call)
        let cancelled = false
        const cancel = call.cancel.bind(call)
        call.cancel = () => {
          cancelled = true
          streams?.delete(call)
          cancel()
        }
        const on = call.on.bind(call)
        call.on = (event: string, callback: (...values: unknown[]) => void) => {
          on(event, (...values: unknown[]) => {
            if (cancelled) return
            if (!isCurrent()) {
              call.cancel()
              return
            }
            if (event === 'end') streams?.delete(call)
            callback(...values)
          })
          return call
        }
        call.on('error', async (err: Error) => {
          const parsedErrors:
            | {
                token: any
                general: any
                void?: boolean
                shouldRefresh?: boolean
              }
            | {
                token: any
                void?: boolean
                shouldRefresh?: boolean
              } = translateTokenError(err)

          if (parsedErrors.token) {
            // If the error is due to token/permission version mismatch, try refreshing first
            if (parsedErrors.shouldRefresh) {
              try {
                const newToken = await attemptTokenRefresh(auth, authDispatch)
                if (newToken || !isCurrent()) {
                  // The next stream/request uses the refreshed session.
                  return
                }
              } catch (refreshErr) {
                log.warn('Stream token refresh could not complete:', refreshErr)
                return
              }
            }
            // If refresh didn't work or wasn't applicable, logout
            authDispatch &&
              authDispatch({
                type: AuthActionType.LOGOUT,
                sourceAccessToken: auth?.accessToken,
                sourceAccountId: auth?.accountId,
                useCase: USE_CASE.SAAS_BUYER,
                role: USER_ROLE.READONLY,
                logoutReason: 'grpc_stream_token_error',
              })
          }

          if (parsedErrors.void === true) {
            return
          }
        })
        return call
      }
    }) as any
  }
  return {
    ...client,
    header,
    accountId: auth?.accountId,
  }
}

export function authorizedGrpcClientFactoryWithContentUpdate<T>(
  clientCons: GrpcClientCons<T>,
  grpcUrl: string,
  auth: AuthRecord,
  authDispatch: Dispatch<AuthAction>,
  content: Content,
  contentUpdate: Dispatch<Content>,
  methodMutator: (asyncCall: unknown) => Promise<unknown>,
  methodRequestMutator: (
    asyncCall: GrpcCall<Message, Message>,
    request: Message,
  ) => Promise<Message>,
  currentAuth?: CurrentAuth,
  currentContent?: () => Content,
): GrpcClient<T> {
  const client = new clientCons(grpcUrl, null, null)
  const scope = getAuthorizationCacheScope(auth)
  const isCurrent = () =>
    !currentAuth ||
    (currentAuth()?.accessToken === auth.accessToken &&
      getAuthorizationCacheScope(currentAuth()) === scope)

  const header = authHeader(auth.accessToken)
  for (const prop in client) {
    if (typeof client[prop] !== 'function') {
      continue
    }

    // eslint-disable-next-line @typescript-eslint/ban-types
    const original = client[prop] as unknown as Function
    client[prop] = ((...args: any[]) => {
      if (!isCurrent() || getRpcAccess(original, auth, args[0]) !== 'allowed')
        return rejectRpc(original)
      const session = createRpcSession(auth, currentAuth, original, args[0])
      const update: Dispatch<Content> = (next) => {
        if (session.isCurrent()) contentUpdate(next)
      }
      let rpcCompleted = false
      const invoke = async () => {
        if (!session.isCurrent()) throw new RpcAccessDenied(original)
        const response = await original.call(client, ...args)
        rpcCompleted = true
        if (!session.isCurrent()) throw new RpcAccessDenied(original)
        return response
      }
      const callWithContent = async () => {
        const response = await callApiAndUpdateContent(
          invoke,
          currentContent?.() ?? content,
          update,
          session.auth as AuthRecord,
          authDispatch,
          (method) => {
            if (!session.isCurrent()) throw new RpcAccessDenied(original)
            return methodMutator(method)
          },
          (method, request) => {
            if (!session.isCurrent()) throw new RpcAccessDenied(original)
            return methodRequestMutator(method, request)
          },
        )
        if (!session.isCurrent()) throw new RpcAccessDenied(original)
        return response
      }
      const metadata = {
        ...args[1],
        ...header,
      }
      args[1] = {
        ...metadata,
        ...getSwrRequestIdHeader(metadata),
      }

      return callWithContent().catch(async (err: Error & { code?: number }) => {
        // A failed follow-up cache refresh must never replay a completed write.
        if (rpcCompleted) throw err
        if (!session.isCurrent() && !session.canRecover(err))
          throw new RpcAccessDenied(original)
        if (err instanceof RpcAccessDenied) throw err
        const parsedErrors:
          | {
              token: any
              general: any
              void?: boolean
              shouldRefresh?: boolean
            }
          | {
              token: any
              void?: boolean
              shouldRefresh?: boolean
            } = translateTokenError(err)

        log.debug(
          'authorizedGrpcClientFactoryWithContentUpdate: error code:',
          err?.code,
          'parsedErrors:',
          parsedErrors,
        )

        if (parsedErrors.token) {
          let logoutToken = auth.accessToken
          // If the error is due to token/permission version mismatch, try refreshing first
          if (parsedErrors.shouldRefresh) {
            log.info(
              'Token/permission version error detected, attempting refresh',
            )
            const newToken = await attemptTokenRefresh(auth, authDispatch)
            if (newToken) {
              await session.acceptRefresh(newToken)
              logoutToken = newToken
              log.info('Retrying original request with refreshed token')
              // Retry the original request with the new token
              try {
                const newHeader = authHeader(newToken)
                const metadata = {
                  ...args[1],
                  ...newHeader,
                }
                args[1] = {
                  ...metadata,
                  ...getSwrRequestIdHeader(metadata),
                }
                return await callWithContent()
              } catch (retryErr) {
                if (rpcCompleted) throw retryErr
                if (!session.isCurrent()) throw new RpcAccessDenied(original)
                if (retryErr instanceof RpcAccessDenied) throw retryErr
                const retryErrors = translateTokenError(retryErr)
                if (!retryErrors.token || retryErrors.shouldRefresh)
                  throw retryErr
              }
            } else {
              log.warn('Token refresh failed, will logout')
            }
          }
          // If refresh didn't work or wasn't applicable, logout
          if (!session.isCurrent()) throw new RpcAccessDenied(original)
          log.info('Logging out due to token error')
          authDispatch({
            type: AuthActionType.LOGOUT,
            sourceAccessToken: logoutToken,
            sourceAccountId: auth.accountId,
            useCase: USE_CASE.SAAS_BUYER,
            role: USER_ROLE.READONLY,
            logoutReason: 'grpc_token_error_with_content_update',
          })
        }

        if (parsedErrors.void === true) {
          return
        }

        throw err
      })
    }) as any
  }
  return {
    ...client,
    header,
    accountId: auth?.accountId,
  }
}

export function useAuthorizedGrpcClient<T>(
  client: GrpcClientCons<T>,
): GrpcClient<T> {
  const { authCtx, authDispatch } = useContext(AuthContext)
  const currentAuth = useCurrentAuth(authCtx)
  const result = useMemo(() => {
    const streams = new Set<{ cancel: () => void }>()
    return {
      client: authorizedGrpcClientFactory(
        client,
        authCtx,
        authDispatch,
        currentAuth,
        streams,
      ),
      dispose: () => streams.forEach((stream) => stream.cancel()),
    }
  }, [authCtx, authDispatch, client, currentAuth])
  useEffect(() => result.dispose, [result])
  return result.client
}

export function useAuthorizedGrpcClientWithContentUpdate<T>(
  client: GrpcClientCons<T>,
): GrpcClient<T> {
  const { authCtx, authDispatch } = useContext(AuthContext)
  const currentAuth = useCurrentAuth(authCtx)
  const { content, contentDispatch } = useContext(ContentContext)
  const methodMutator = useGrpcRevalidateByMethod()
  const methodRequestMutator = useGrpcRevalidateByMethodRequest()
  const latest = useRef({ content, methodMutator, methodRequestMutator })
  latest.current = { content, methodMutator, methodRequestMutator }

  return useMemo(
    () =>
      authorizedGrpcClientFactoryWithContentUpdate(
        client,
        NTRCE_API_URL,
        authCtx,
        authDispatch,
        content,
        contentDispatch,
        (method) => latest.current.methodMutator(method),
        (method, request) =>
          latest.current.methodRequestMutator(method, request),
        currentAuth,
        () => latest.current.content,
      ),
    [client, authCtx, authDispatch, content, contentDispatch, currentAuth],
  )
}

// Preserve the generated client's return contract when denying a stream. Emit
// the normal error/end events asynchronously so consumers can attach handlers.
function rejectRpc(method: unknown) {
  const error = new RpcAccessDenied(method)
  if (!rpcAuthorization[getRpcPath(method) || '']?.stream)
    return Promise.reject(error)
  const handlers = new Map<string, Array<(value?: unknown) => void>>()
  let cancelled = false
  const stream = {
    on(event: string, handler: (value?: unknown) => void) {
      handlers.set(event, [...(handlers.get(event) || []), handler])
      return stream
    },
    cancel() {
      cancelled = true
    },
  }
  Promise.resolve().then(() => {
    if (cancelled) return
    handlers.get('error')?.forEach((handler) => handler(error))
    handlers.get('end')?.forEach((handler) => handler())
  })
  return stream
}
